import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  Button,
  CircularProgress,
  Stack,
  useTheme
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Email
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const EmailVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setError('Token de vérification manquant');
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/auth/verify-email?token=${token}`);
        
        if (response.data.success) {
          setSuccess(true);
          setUserInfo(response.data.user);
        } else {
          setError(response.data.message || 'Erreur lors de la vérification');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Erreur lors de la vérification de l\'email');
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const handleGoToHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%', textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              Vérification en cours...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Veuillez patienter pendant que nous vérifions votre email.
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          {success ? (
            <Box textAlign="center">
              <Typography variant="h4" gutterBottom color="success.main">
                Votre adresse email a été vérifiée avec succès !
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Félicitations {userInfo?.name} !
              </Typography>
              
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Votre adresse email a été vérifiée avec succès. 
                {userInfo?.isApproved 
                  ? 'Votre compte est approuvé et vous pouvez maintenant vous connecter.'
                  : 'Votre compte est en attente d\'approbation de nos services.'
                }
              </Typography>

              {userInfo?.isApproved ? (
                <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                  <Typography variant="body2">
                    <strong>Votre compte est prêt !</strong><br/>
                    • Votre email est vérifié<br/>
                    • Votre compte est approuvé<br/>
                    • Vous pouvez maintenant vous connecter et utiliser la plateforme
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                  <Typography variant="body2">
                    <strong>Prochaines étapes :</strong><br/>
                    • Votre compte sera examiné par notre équipe<br/>
                    • Vous recevrez un email de confirmation une fois approuvé<br/>
                    • Vous pourrez alors vous connecter et utiliser la plateforme
                  </Typography>
                </Alert>
              )}

              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  onClick={handleGoToHome}
                  sx={{ minWidth: 120 }}
                >
                  Accueil
                </Button>
                {userInfo?.isApproved && (
                  <Button
                    variant="contained"
                    onClick={handleGoToLogin}
                    sx={{ minWidth: 120 }}
                    color="success"
                  >
                    Se connecter
                  </Button>
                )}
                {!userInfo?.isApproved && (
                  <Button
                    variant="outlined"
                    onClick={handleGoToLogin}
                    sx={{ minWidth: 120 }}
                    disabled
                  >
                    En attente d'approbation
                  </Button>
                )}
              </Stack>
            </Box>
          ) : (
            <Box textAlign="center">
              <Typography variant="h4" gutterBottom color="error.main">
                Erreur de vérification
              </Typography>
              
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Le lien de vérification est invalide ou a expiré. 
                Veuillez contacter le support si le problème persiste.
              </Typography>

              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  onClick={handleGoToHome}
                  sx={{ minWidth: 120 }}
                >
                  Accueil
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleGoToLogin}
                  sx={{ 
                    minWidth: 120,
                    background: theme.palette.color.degradeOne,
                    color: 'white',
                    '&:hover': {
                      background: theme.palette.color.degradeOne,
                      opacity: 0.9
                    }

                  }}
                >
                  Connexion
                </Button>
              </Stack>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default EmailVerification;
